/**
 * Zone Filter - Site-wide climate zone filtering using localStorage
 * Integrates with Shopify Search & Discovery app using GID-based filtering
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'selectedClimateZoneGID';
  const STORAGE_KEY_NAME = 'selectedClimateZoneName';

  // Get/Set localStorage helpers
  function getSelectedZone() {
    try {
      return {
        gid: localStorage.getItem(STORAGE_KEY) || '',
        name: localStorage.getItem(STORAGE_KEY_NAME) || ''
      };
    } catch (e) {
      console.warn('localStorage not available:', e);
      return { gid: '', name: '' };
    }
  }

  function setSelectedZone(gid, name) {
    try {
      if (gid) {
        localStorage.setItem(STORAGE_KEY, gid);
        localStorage.setItem(STORAGE_KEY_NAME, name);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_KEY_NAME);
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }

  // Get zone name from dropdown option
  function getZoneNameFromGID(gid) {
    const dropdown = document.getElementById('zone-selector-dropdown');
    if (!dropdown) return '';

    const option = dropdown.querySelector(`option[value="${gid}"]`);
    return option ? option.dataset.zoneName : '';
  }

  // Update UI based on selected zone
  function updateZoneUI(zone) {
    const dropdown = document.getElementById('zone-selector-dropdown');
    const indicator = document.getElementById('zone-indicator');
    const indicatorText = indicator?.querySelector('.zone-indicator__text');

    if (dropdown) {
      const nextValue = zone.gid || '';
      // Only fire change when the value really changes. handleZoneChange calls this
      // function, so an unconditional dispatch here loops until the stack overflows.
      if (dropdown.value !== nextValue) {
        dropdown.value = nextValue;
        // Trigger change event so header button updates
        dropdown.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (indicator && indicatorText) {
      if (zone.gid && zone.name) {
        indicatorText.textContent = zone.name;
        indicator.style.display = 'flex';
      } else {
        indicator.style.display = 'none';
      }
    }
  }

  // Apply zone filter to current collection page
  function applyZoneFilter(skipReload = false) {
    const selectedZone = getSelectedZone();

    // Only apply filter on collection pages
    if (!window.location.pathname.includes('/collections/')) {
      return;
    }

    // If no zone selected, don't modify URL
    if (!selectedZone.gid) {
      return;
    }

    const url = new URL(window.location.href);
    const currentFilter = url.searchParams.get('filter.p.m.custom.zone');

    // If filter is already applied, don't reload
    if (currentFilter === selectedZone.gid) {
      return;
    }

    // Preserve existing filter parameters if they exist, or add empty ones
    if (!url.searchParams.has('filter.v.price.gte')) {
      url.searchParams.set('filter.v.price.gte', '');
    }
    if (!url.searchParams.has('filter.v.price.lte')) {
      url.searchParams.set('filter.v.price.lte', '');
    }
    
    url.searchParams.delete('page');

    // Apply the filter using GID
    url.searchParams.set('filter.p.m.custom.zone', selectedZone.gid);

    // Add sort_by if not present
    if (!url.searchParams.has('sort_by')) {
      url.searchParams.set('sort_by', 'manual');
    }

    console.log('Applying filter with URL:', url.toString());

    // Update URL without page reload using History API
    history.pushState({}, '', url.toString());

    // Trigger Shopify's facet filtering update
    triggerFacetUpdate();
  }

  // Update the zone checkbox to match the selected zone
  function updateZoneCheckbox() {
    const selectedZone = getSelectedZone();

    // Find all zone checkboxes in the filter form
    const zoneCheckboxes = document.querySelectorAll('input[name="filter.p.m.custom.zone"]');

    zoneCheckboxes.forEach(checkbox => {
      if (selectedZone.gid && checkbox.value === selectedZone.gid) {
        checkbox.checked = true;
        console.log('Zone checkbox checked:', checkbox.value);
      } else if (!selectedZone.gid) {
        checkbox.checked = false;
      }
    });
  }

  // Trigger Shopify Search & Discovery to update products
  function triggerFacetUpdate() {
    // Just fetch products using the current URL search params
    // This ensures our zone filter is included
    console.log('Triggering facet form update...');
    fetchFilteredProducts(window.location.search);
  }

  // Fetch filtered products and update the DOM
  function fetchFilteredProducts(queryString) {
    // Remove leading ? if present
    const cleanQuery = queryString ? queryString.replace(/^\?/, '') : '';
    const url = `${window.location.pathname}${cleanQuery ? '?' + cleanQuery : ''}`;

    console.log('Fetching filtered products from:', url);

    fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        console.log('Response received, parsing...');

        // Try multiple selectors for product grid
        const productGridSelectors = [
          '#ProductGridContainer',
          '#product-grid',
          '.collection',
          '[id*="product-grid"]',
          'main .collection-product-grid',
          '.collection-grid',
          '#CollectionProductGrid'
        ];

        let newProductGrid = null;
        let currentProductGrid = null;

        for (const selector of productGridSelectors) {
          newProductGrid = doc.querySelector(selector);
          currentProductGrid = document.querySelector(selector);

          if (newProductGrid && currentProductGrid) {
            console.log('Found product grid with selector:', selector);
            currentProductGrid.innerHTML = newProductGrid.innerHTML;
            console.log('Product grid updated');
            break;
          }
        }

        if (!newProductGrid || !currentProductGrid) {
          console.warn('Product grid not found, available elements:',
            Array.from(doc.querySelectorAll('[id], [class*="product"], [class*="collection"]'))
              .map(el => `${el.tagName}#${el.id}.${el.className}`)
          );
        }

        // Update active filters display - update ALL facet-remove elements
        const newFacetRemoves = doc.querySelectorAll('facet-remove');
        const currentFacetRemoves = document.querySelectorAll('facet-remove');

        if (newFacetRemoves.length > 0 && currentFacetRemoves.length > 0) {
          currentFacetRemoves.forEach((current, index) => {
            if (newFacetRemoves[index]) {
              current.innerHTML = newFacetRemoves[index].innerHTML;
            }
          });
          console.log('Active filters (facet-remove) updated');
        }

        // Update product count (update all instances for both mobile and desktop)
        const newProductCounts = doc.querySelectorAll('.product-count');
        const currentProductCounts = document.querySelectorAll('.product-count');

        if (newProductCounts.length > 0 && currentProductCounts.length > 0) {
          currentProductCounts.forEach((current, index) => {
            if (newProductCounts[index]) {
              current.innerHTML = newProductCounts[index].innerHTML;
            }
          });
          console.log('Product count updated (mobile and desktop)');
        }

        // Update active facets containers
        const activeFiltersSelectors = [
          '.active-facets',
          '.active-facets-desktop',
          '.active-facets-mobile',
          '.active-facets-vertical-filter',
          '.active-filters'
        ];

        for (const selector of activeFiltersSelectors) {
          const newElement = doc.querySelector(selector);
          const currentElement = document.querySelector(selector);

          if (newElement && currentElement) {
            currentElement.innerHTML = newElement.innerHTML;
            console.log(`Updated ${selector}`);
          }
        }

        // DON'T update the filter form - this would destroy Search & Discovery's event listeners
        // Instead, manually update our zone checkbox to show as checked
        updateZoneCheckbox();

        console.log('DOM update complete');
      })
      .catch(error => {
        console.error('Error fetching filtered products:', error);
        console.log('Falling back to full page reload');
        // Fallback to full page reload
        window.location.href = url;
      });
  }

  // Handle zone selection change
  function handleZoneChange(event) {
    const selectedGID = event.target.value;
    const selectedOption = event.target.options[event.target.selectedIndex];
    const selectedName = selectedOption.dataset.zoneName || '';

    console.log('Zone changed:', { gid: selectedGID, name: selectedName });

    // Save to localStorage
    setSelectedZone(selectedGID, selectedName);

    // Update UI
    updateZoneUI({ gid: selectedGID, name: selectedName });

    // Apply filter if on collection page
    if (window.location.pathname.includes('/collections/')) {
      console.log('On collection page, applying filter...');
      applyZoneFilter();
    } else {
      console.log('Not on collection page, filter not applied');
    }
  }

  // Handle clear zone button
  function handleClearZone() {
    console.log('Clearing zone filter...');

    // Clear localStorage
    setSelectedZone('', '');

    // Update UI
    updateZoneUI({ gid: '', name: '' });

    // Remove filter from URL if on collection page
    if (window.location.pathname.includes('/collections/')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('filter.p.m.custom.zone');

      // If no other filters remain, also remove the empty price filters
      const hasOtherFilters = Array.from(url.searchParams.keys()).some(
        key => key.startsWith('filter.') && key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte'
      );

      if (!hasOtherFilters) {
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
      }

      console.log('Clearing filter, new URL:', url.toString());

      // Update URL without page reload using History API
      history.pushState({}, '', url.toString());

      // Trigger Shopify's facet filtering update
      triggerFacetUpdate();
    }
  }

  // Initialize on page load
  function init() {
    console.log('Zone filter initializing...');

    const dropdown = document.getElementById('zone-selector-dropdown');
    const clearBtn = document.getElementById('clear-zone-btn');

    if (!dropdown) {
      console.warn('Zone dropdown not found');
      return; // Header not loaded yet
    }

    console.log('Zone dropdown found, options count:', dropdown.options.length);

    // Load saved zone
    const savedZone = getSelectedZone();
    console.log('Saved zone from localStorage:', savedZone);

    // If we have a GID but no name, fetch the name from the dropdown
    if (savedZone.gid && !savedZone.name) {
      savedZone.name = getZoneNameFromGID(savedZone.gid);
      if (savedZone.name) {
        setSelectedZone(savedZone.gid, savedZone.name);
      }
    }

    updateZoneUI(savedZone);

    // Update the zone checkbox to match saved selection
    updateZoneCheckbox();

    // Apply filter to collection pages
    if (savedZone.gid) {
      applyZoneFilter();
    }

    // Add event listeners
    dropdown.addEventListener('change', handleZoneChange);
    console.log('Event listener added to dropdown');

    if (clearBtn) {
      clearBtn.addEventListener('click', handleClearZone);
      console.log('Event listener added to clear button');
    }

    console.log('Zone filter initialized successfully');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
